/**
 * Storage connections ("sources"): an organization's own storage the team can
 * browse and import from, such as an S3-compatible bucket or a NAS share that
 * one of the organization's workers has mounted.
 *
 * Every path that crosses a boundary (browser → API, API → worker, reducer
 * arguments) is a path *relative to the connection's root* (its bucket prefix,
 * or the worker's shared folder). These helpers normalize and validate such
 * paths so nothing can point outside the configured root.
 */
import { extOf, mediaMimeForExt } from './storage-keys';
import { fontMimeForExt } from './fonts';

export const SOURCE_KINDS = ['s3', 'worker'] as const;
export type SourceKind = (typeof SOURCE_KINDS)[number];

export const SOURCE_KIND_LABELS: Record<SourceKind, string> = {
  s3: 'S3-compatible bucket',
  worker: 'Network folder (on one of your workers)',
};

/** Longest relative path accepted (S3 keys are at most 1024 bytes). */
export const MAX_SOURCE_PATH = 1000;
/** Most entries one listing returns (S3 pages are 1000 keys). */
export const SOURCE_LIST_LIMIT = 1000;
/** Most files one import may bring in. */
export const MAX_SOURCE_IMPORT = 200;

/** Non-secret settings of an S3-compatible connection (visible to members). */
export interface S3SourceConfig {
  /** '' = AWS S3; otherwise e.g. https://<account>.r2.cloudflarestorage.com or http://nas:9000 (MinIO). */
  endpoint: string;
  region: string;
  bucket: string;
  /** Only keys under this prefix are visible ('' = the whole bucket; else ends with '/'). */
  prefix: string;
  /** Path-style URLs (MinIO, some on-premises gateways). */
  forcePathStyle: boolean;
  /** Access key id: not secret by itself, shown so admins can tell credentials apart. */
  accessKeyId: string;
}

/** A folder an organization's worker shares (its SOURCE_ROOTS), and where inside it the connection starts. */
export interface WorkerSourceConfig {
  workerId: string;
  /** Name of one of the worker's shared folders (never a path on the worker's machine). */
  root: string;
  /** Subfolder of that root the connection starts at ('' = all of it). */
  path: string;
}

/** One entry of a listing. `path` is relative to the connection's root. */
export interface SourceEntry {
  name: string;
  path: string;
  dir: boolean;
  size: number;
  /** Last modified, ms since the epoch (0 = unknown). */
  modifiedAt: number;
}

export interface SourceListing {
  path: string;
  entries: SourceEntry[];
  /** More entries follow: pass it back as `cursor`. */
  cursor?: string;
}

/**
 * Normalizes a path relative to a connection's root: no leading or trailing
 * slashes, no empty, `.` or `..` segments, no backslashes, NUL or control
 * characters. '' is the root itself. Returns null for anything else.
 */
export function normalizeSourcePath(raw: string): string | null {
  if (typeof raw !== 'string') return null;
  if (raw.length > MAX_SOURCE_PATH) return null;
  // eslint-disable-next-line no-control-regex
  if (/[\u0000-\u001f\u007f\\]/.test(raw)) return null;
  const parts = raw.split('/').filter((p) => p !== '');
  for (const p of parts) if (p === '.' || p === '..') return null;
  return parts.join('/');
}

/** A bucket prefix: '' or segments ending with '/'. Null when invalid. */
export function normalizePrefix(raw: string): string | null {
  const p = normalizeSourcePath(raw ?? '');
  if (p === null) return null;
  return p ? `${p}/` : '';
}

/** The full object key of a relative path under a prefix. */
export function sourceObjectKey(prefix: string, rel: string): string {
  const p = normalizePrefix(prefix);
  const r = normalizeSourcePath(rel);
  if (p === null || r === null) throw new Error('Invalid path');
  return p + r;
}

/** The relative path of an object key, or null when it is outside the prefix. */
export function relativeToPrefix(prefix: string, key: string): string | null {
  if (!key.startsWith(prefix)) return null;
  const rel = normalizeSourcePath(key.slice(prefix.length));
  return rel === null || prefix + rel !== key.replace(/\/+$/, '') ? null : rel;
}

/** Joins a folder and a name into a relative path (null when invalid). */
export function joinSourcePath(dir: string, name: string): string | null {
  return normalizeSourcePath(dir ? `${dir}/${name}` : name);
}

/** The last segment of a relative path. */
export function sourceBaseName(rel: string): string {
  const i = rel.lastIndexOf('/');
  return i < 0 ? rel : rel.slice(i + 1);
}

/** The folder of a relative path ('' at the root). */
export function sourceDirName(rel: string): string {
  const i = rel.lastIndexOf('/');
  return i < 0 ? '' : rel.slice(0, i);
}

// ---------------------------------------------------------------------------
// Linked media ("link in place")
// ---------------------------------------------------------------------------

/**
 * Storage key of an original that stays in a connection: `src/<sourceId>/<path>`.
 * It never starts with `orgs/`, so nothing that writes or garbage-collects
 * vidmakr's own storage can touch it.
 */
export function linkedKey(sourceId: string, rel: string): string {
  if (!isSourceId(sourceId)) throw new Error('Invalid connection id');
  const r = normalizeSourcePath(rel);
  if (!r) throw new Error('Invalid path');
  return `src/${sourceId}/${r}`;
}

export function parseLinkedKey(key: string): { sourceId: string; path: string } | null {
  const m = /^src\/([A-Za-z0-9_-]{8,64})\/(.+)$/.exec(key);
  if (!m) return null;
  const path = normalizeSourcePath(m[2]);
  return path && path === m[2] ? { sourceId: m[1], path } : null;
}

/** An original linked from a bucket (the API streams it; workers get presigned URLs). */
export function isLinkedKey(key: string): boolean {
  return key.startsWith('src/');
}

export function isSourceId(id: string): boolean {
  return /^[A-Za-z0-9_-]{8,64}$/.test(id);
}

// ---------------------------------------------------------------------------
// Media linked from a network folder (kept on the NAS)
// ---------------------------------------------------------------------------

/**
 * Storage key of an original that stays in a worker's shared folder:
 * `nas/<sourceId>/<path>`, the path relative to the connection's folder.
 * It is a marker, not an object: no storage holds it and the API never hands
 * out a URL for it. Only the worker that shares the folder reads the file,
 * from disk, in jobs pinned to it (proxies, conversions, exports); everything
 * else uses the proxies.
 */
export function folderLinkedKey(sourceId: string, rel: string): string {
  if (!isSourceId(sourceId)) throw new Error('Invalid connection id');
  const r = normalizeSourcePath(rel);
  if (!r) throw new Error('Invalid path');
  return `nas/${sourceId}/${r}`;
}

export function parseFolderLinkedKey(key: string): { sourceId: string; path: string } | null {
  const m = /^nas\/([A-Za-z0-9_-]{8,64})\/(.+)$/.exec(key);
  if (!m) return null;
  const path = normalizeSourcePath(m[2]);
  return path && path === m[2] ? { sourceId: m[1], path } : null;
}

export function isFolderLinkedKey(key: string): boolean {
  return key.startsWith('nas/');
}

/** The file a job reads from a worker's shared folder: the folder's name and the path inside it. */
export interface FolderFile {
  root: string;
  path: string;
}

/** Where jobs on cloud workers read an asset: its original, or for media kept on a NAS its proxy ('' = none yet). */
export function cloudReadableKey(a: { storageKey: string; proxyKey: string }): string {
  return isFolderLinkedKey(a.storageKey) ? a.proxyKey : a.storageKey;
}

/** The worker a job using media linked from network folders must run on, and the files it reads there. */
export interface FolderPlan {
  workerId: string;
  workerName: string;
  /** By storage key. */
  files: Record<string, FolderFile>;
}

interface PlanSource {
  name: string;
  kind: string;
  config: string;
}

interface PlanWorker {
  name: string;
  disabled: boolean;
  /** Names of the folders it shares (undefined = not known here, not checked). */
  roots?: string[];
}

/**
 * Resolves the media of a job that are linked from network folders: all of
 * them must be on one worker, which must still exist, be enabled and share
 * the folder. Null when the job uses no such media; `{ error }` when it
 * cannot run. Used by the module (authoritative) and the export dialog.
 */
export function planFolderMedia(
  assets: Iterable<{ name: string; storageKey: string }>,
  sourceOf: (id: string) => PlanSource | null | undefined,
  workerOf: (id: string) => PlanWorker | null | undefined,
): FolderPlan | { error: string } | null {
  let plan: FolderPlan | null = null;
  let firstSource = '';
  for (const a of assets) {
    if (!isFolderLinkedKey(a.storageKey)) continue;
    const k = parseFolderLinkedKey(a.storageKey);
    if (!k) return { error: `"${a.name}" has an invalid link to its network folder` };
    const src = sourceOf(k.sourceId);
    if (!src) return { error: `"${a.name}" is linked from a network folder connection that was removed; import it again` };
    const c = src.kind === 'worker' ? (parseSourceConfig('worker', src.config) as WorkerSourceConfig | null) : null;
    if (!c) return { error: `"${a.name}" is linked from "${src.name}", which is no longer a network folder` };
    const w = workerOf(c.workerId);
    if (!w) return { error: `The worker sharing "${src.name}" (where "${a.name}" is) was removed` };
    if (w.disabled) return { error: `The worker "${w.name}" that shares "${src.name}" (where "${a.name}" is) is disabled` };
    if (w.roots && !w.roots.includes(c.root)) return { error: `The worker "${w.name}" no longer shares the folder "${c.root}" (where "${a.name}" is)` };
    const path = joinSourcePath(c.path, k.path);
    if (!path) return { error: `"${a.name}" has an invalid path` };
    if (plan && plan.workerId !== c.workerId) {
      return {
        error:
          `This uses media linked from network folders on two workers ("${plan.workerName}" for "${firstSource}" and "${w.name}" for "${src.name}"). ` +
          'It can only run on the one worker that has all the files: copy the media of one of them into vidmakr, or keep it all on one worker.',
      };
    }
    plan ??= { workerId: c.workerId, workerName: w.name, files: {} };
    firstSource ||= src.name;
    plan.files[a.storageKey] = { root: c.root, path };
  }
  return plan;
}

// ---------------------------------------------------------------------------
// Worker shared folders
// ---------------------------------------------------------------------------

export interface SourceRoot {
  name: string;
  path: string;
}

/** Names of shared folders: letters, digits, space, dot, dash, underscore. */
export function isRootName(name: string): boolean {
  return /^[A-Za-z0-9][A-Za-z0-9 _.-]{0,39}$/.test(name);
}

/**
 * Parses a worker's SOURCE_ROOTS: `NAS=/mnt/nas,Archive=/Volumes/Archive`
 * (`;` also separates, for Windows paths with commas: `NAS=\\server\share;…`).
 * Invalid or duplicate names are skipped.
 */
export function parseSourceRoots(raw: string): SourceRoot[] {
  const out: SourceRoot[] = [];
  for (const part of (raw ?? '').split(/[;,\n]/)) {
    const i = part.indexOf('=');
    if (i <= 0) continue;
    const name = part.slice(0, i).trim();
    const path = part.slice(i + 1).trim();
    if (!isRootName(name) || !path || out.some((r) => r.name === name)) continue;
    out.push({ name, path });
  }
  return out;
}

// ---------------------------------------------------------------------------
// Files
// ---------------------------------------------------------------------------

/** The library kind and MIME type of a file name that can be imported, or null. */
export function importableFile(name: string): { kind: 'video' | 'audio' | 'image' | 'font' | 'lut'; mime: string; ext: string } | null {
  const ext = extOf(name);
  if (!ext) return null;
  if (ext === 'cube') return { kind: 'lut', mime: 'application/x-cube-lut', ext };
  const font = fontMimeForExt(ext);
  if (font) return { kind: 'font', mime: font, ext };
  const mime = mediaMimeForExt(ext);
  if (!mime) return null;
  const kind = mime.startsWith('video/') ? 'video' : mime.startsWith('audio/') ? 'audio' : 'image';
  return { kind, mime, ext };
}

/** Validates S3 settings; returns the normalized config or an error message. */
export function validateS3Config(c: Partial<S3SourceConfig>): S3SourceConfig | string {
  const bucket = String(c.bucket ?? '').trim();
  if (!/^[a-z0-9][a-z0-9.-]{1,61}[a-z0-9]$/.test(bucket) && !/^[A-Za-z0-9._-]{1,255}$/.test(bucket)) return 'Enter a valid bucket name';
  const endpoint = String(c.endpoint ?? '').trim().replace(/\/+$/, '');
  // No URL class here: this also runs inside the database module.
  if (endpoint) {
    if (!/^https?:\/\//i.test(endpoint)) return 'The endpoint must start with https:// or http://';
    if (/[\s?#@\\]/.test(endpoint)) return 'The endpoint must not contain credentials or a query';
    if (!/^https?:\/\/(\[[0-9a-fA-F:.]+\]|[A-Za-z0-9.-]+)(:\d{1,5})?(\/[A-Za-z0-9._~\-/%]*)?$/i.test(endpoint)) return 'The endpoint must be a URL like https://s3.example.com';
  }
  const region = String(c.region ?? '').trim() || (endpoint ? 'auto' : 'us-east-1');
  if (!/^[a-z0-9-]{1,40}$/.test(region)) return 'Enter a valid region (e.g. us-east-1 or auto)';
  const prefix = normalizePrefix(String(c.prefix ?? ''));
  if (prefix === null) return 'The prefix must be a folder path without "." or ".." parts';
  const accessKeyId = String(c.accessKeyId ?? '').trim();
  if (accessKeyId.length > 256 || /\s/.test(accessKeyId)) return 'Invalid access key id';
  return { endpoint, region, bucket, prefix, forcePathStyle: !!c.forcePathStyle, accessKeyId };
}

/** Parses a connection's stored config JSON (tolerant: unknown fields are dropped). */
export function parseSourceConfig(kind: string, json: string): S3SourceConfig | WorkerSourceConfig | null {
  try {
    const c = JSON.parse(json || '{}');
    if (kind === 's3') {
      const v = validateS3Config(c);
      return typeof v === 'string' ? null : v;
    }
    if (kind === 'worker') {
      const path = normalizeSourcePath(String(c.path ?? ''));
      if (typeof c.workerId !== 'string' || !isRootName(String(c.root ?? '')) || path === null) return null;
      return { workerId: c.workerId, root: c.root, path };
    }
  } catch {
    /* invalid */
  }
  return null;
}
